'use client';

import { useEffect, useRef, useState } from 'react';

interface StatCounterProps {
  /** Full display string, e.g. "₹500Cr+", "30+", "7X". */
  value: string;
  className?: string;
}

interface ParsedValue {
  prefix: string;
  number: number;
  suffix: string;
}

// Splits "₹500Cr+" into prefix "₹", the number 500, and suffix "Cr+" so only the
// numeric part animates — everything else in the string stays fixed throughout.
function parseValue(value: string): ParsedValue | null {
  const match = value.match(/^(\D*?)(\d+(?:\.\d+)?)(\D*)$/);
  if (!match) return null;
  return { prefix: match[1], number: parseFloat(match[2]), suffix: match[3] };
}

export default function StatCounter({ value, className }: StatCounterProps) {
  const parsed = parseValue(value);
  // Initial state (and therefore the server-rendered HTML, before hydration) is always the
  // final value — the count-up below only ever animates into this same end state, never
  // starting from 0. A value this component can't parse (no leading/trailing number) just
  // renders as-is with no animation.
  const [display, setDisplay] = useState(value);
  const hasAnimatedRef = useRef(false);
  const elRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!parsed) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;

    const el = elRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasAnimatedRef.current) return;
        hasAnimatedRef.current = true;

        const target = parsed.number;
        const isWhole = Number.isInteger(target);
        const format = (n: number) => `${parsed.prefix}${isWhole ? Math.round(n) : n.toFixed(1)}${parsed.suffix}`;

        const duration = 1400;
        const steps = 40;
        const intervalTime = duration / steps;
        // Starts the count-up at 40% of the target, not 0 — a brief flourish rather than a
        // multi-second climb from zero, and it keeps the "never starts from 0" rule true for
        // every frame, not just the initial server-rendered markup.
        let current = target * 0.4;
        const stepAmount = (target - current) / steps;
        setDisplay(format(current));

        const interval = setInterval(() => {
          current += stepAmount;
          if (current >= target) {
            clearInterval(interval);
            setDisplay(value);
            return;
          }
          setDisplay(format(current));
        }, intervalTime);
      },
      { threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [parsed, value]);

  return (
    <span ref={elRef} className={className}>
      {display}
    </span>
  );
}
